import styled, { keyframes } from "styled-components";
import { useEffect, useState } from "react";
import { useSocket, useCursors, useSessionLabels } from "../../context/SocketContext";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Layer = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 9999;
  overflow: hidden;
`;

const CursorWrapper = styled.div`
  position: absolute;
  transition: left 0.1s linear, top 0.1s linear;
  animation: ${fadeIn} 0.2s ease;
`;

const CursorSvg = styled.svg`
  width: 16px;
  height: 20px;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));
`;

const CursorLabel = styled.div`
  position: absolute;
  top: 18px;
  left: 12px;
  background-color: ${props => props.$color || '#6b7280'};
  color: white;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;
  line-height: 1.3;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
`;

const CURSOR_STALE_MS = 5000;

// 영역 앵커 기반 위치 계산 — 송신 측이 올라가 있던 UI 영역(anchor)을 수신 측
// 화면에서 찾아, 그 영역의 실제 크기에 상대 비율(rx, ry)을 곱해 위치를 복원한다.
// 창 크기/해상도/사이드바 폭이 서로 달라도 같은 UI 지점 위에 표시됨.
function resolveCursorPosition(c) {
  if (c.anchor) {
    const el = document.querySelector(`[data-cursor-anchor="${c.anchor}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        return { left: rect.left + c.rx * rect.width, top: rect.top + c.ry * rect.height };
      }
    }
    return null; // 수신 측 화면에 해당 영역이 없으면 표시하지 않음
  }
  if (c.fx != null && c.fy != null) {
    return { left: c.fx * window.innerWidth, top: c.fy * window.innerHeight };
  }
  return null;
}

/**
 * 전역 커서 레이어 — 타임라인 밖(사이드바/헤더/테이블 등)에서의 마우스 위치 공유.
 * 1순위: data-cursor-anchor 영역 내 상대 비율 (반응형 자동 보정, 정확)
 * 2순위: 화면 비율 좌표 fx/fy (앵커 영역 밖일 때 근사 표시)
 * 타임라인(data-cursor-zone="timeline") 위에서는 각 차트의 CursorOverlay가
 * 날짜 기반으로 정밀 표시하므로 여기서는 전송하지 않는다.
 */
export default function GlobalCursorLayer({ currentView }) {
  const { emitCursorMove, currentChartId, socketId } = useSocket();
  const remoteCursors = useCursors();
  const labels = useSessionLabels();
  const [now, setNow] = useState(() => Date.now());

  // 타임라인 밖 마우스 움직임 → 앵커 상대 좌표(우선) 또는 화면 비율 좌표 전송
  useEffect(() => {
    if (!currentChartId) return;
    const onMove = (e) => {
      if (!e.target || !e.target.closest) return;
      if (e.target.closest('[data-cursor-zone="timeline"]')) return;

      const anchorEl = e.target.closest('[data-cursor-anchor]');
      if (anchorEl) {
        const rect = anchorEl.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          emitCursorMove({
            view: 'screen',
            anchor: anchorEl.getAttribute('data-cursor-anchor'),
            rx: (e.clientX - rect.left) / rect.width,
            ry: (e.clientY - rect.top) / rect.height,
          });
          return;
        }
      }
      emitCursorMove({
        view: 'screen',
        fx: e.clientX / window.innerWidth,
        fy: e.clientY / window.innerHeight,
      });
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [currentChartId, emitCursorMove]);

  // 오래 안 움직인 커서 페이드아웃 + 리사이즈 시 위치 재계산
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    const onResize = () => setNow(Date.now());
    window.addEventListener('resize', onResize);
    return () => { clearInterval(t); window.removeEventListener('resize', onResize); };
  }, []);

  const entries = Object.entries(remoteCursors)
    .filter(([key, c]) => {
      // 지금 이 탭(내 소켓)의 커서만 제외 — 내 계정의 다른 브라우저 커서는 표시
      if (socketId && (c.socketId === socketId || key === socketId)) return false;
      if (now - c.timestamp > CURSOR_STALE_MS) return false;
      // 'screen' 커서는 여기서 표시.
      // 차트 커서(classic/wbs)는 같은 뷰면 CursorOverlay가 정밀 표시하므로 제외하고,
      // 뷰가 다르면 fx/fy 화면 비율로 폴백 표시 — 커서가 사라지지 않게 함
      if (c.view === 'screen') return true;
      if (c.view && currentView && c.view !== currentView) return c.fx != null;
      return false;
    })
    .map(([key, c]) => [key, c, resolveCursorPosition(c)])
    .filter(([, , pos]) => pos !== null);

  if (entries.length === 0) return null;

  return (
    <Layer>
      {entries.map(([key, c, pos]) => (
        <CursorWrapper
          key={key}
          style={{
            left: pos.left,
            top: pos.top,
          }}
        >
          <CursorSvg viewBox="0 0 16 20">
            <path
              d="M0 0 L0 16 L4.5 12 L8.5 19 L11 18 L7 11 L13 11 Z"
              fill={c.cursorColor || '#6b7280'}
              stroke="white"
              strokeWidth="1"
            />
          </CursorSvg>
          <CursorLabel $color={c.cursorColor}>
            {labels[c.socketId] || c.userName}
          </CursorLabel>
        </CursorWrapper>
      ))}
    </Layer>
  );
}
